/* eslint-disable @typescript-eslint/camelcase */
declare const window: any;


// debug logging only if ?analyticsDebug=1 is present in the URL of the current window or parent window (in the case of iframe)
const _isAnalyticsDebugEnabled = (() => {
    const hasParam = (w: Window | null | undefined): boolean => {
        try {
            const search = w && w.location && w.location.search ? w.location.search : false;
            if (typeof search !== 'string') {
                return false;
            }
            const params = new URLSearchParams(search);
            return params.get('analyticsDebug') === 'true';
        }
        catch {
            return false; // cross-origin parent apod.
        }
    };

    // actual window
    if (hasParam(window)) {
        return true;
    }
    // parent
    try {
        if (window.parent && window.parent !== window) {
            if (hasParam(window.parent)) {
                return true;
            }
        }
    }
    catch {
        // ignore cross-origin
    }
    return false;
})();


/**
* ===================================================================================
* Returns page data for analytics events stored in generic data object or in a specific data object for Tealium
* ===================================================================================
 */

interface IAnalyticsData {
    [key: string]: string | number | null | undefined;
}

export function getAnalyticsPageData(): IAnalyticsData {
    if (window.analyticsData) {
        return window.analyticsData; // generic data container (Google format)
    }
    if (window.utag_data) {
        return window.utag_data; // temporal fallback data, it should be removed with Tealium
    }

    return {};
}


/**
 * ===================================================================================
 *  Central Analytics Dispatcher of all events for all platforms
 * ===================================================================================
 */
function trackAnalyticsAction(data: IAnalyticsEvent | IAnalyticsView): void {

    if (_isAnalyticsDebugEnabled) {
        console.log('---- \n trackAnalyticsAction', data);
    }

    if ((!data.event || data.event.trim() === '') && data.on_page_event) {
        data.event = data.on_page_event;
    }

    // make sure that a property "event" is sent (GTM only reacts when your dataLayer.push() includes the special key: event: "something")
    if (!data.event || data.event.trim() === '') {
        console.error('trackAnalyticsAction: invalid event attribute.', { data });
        return;
    }

    // 1. Sends data to the Google Tag Manager Data Layer
    const dataLayer = window.dataLayer || [];
    if (_isAnalyticsDebugEnabled) {
        console.log('sending dataLayer.push()');
    }
    dataLayer.push(data);

    // 2. Sends data to Tealium
    const uTag = window.utag;
    if (uTag) {
        // Tealium uses different methods for page views and other events:

        if (data.page_name) { // It's "page view" if the page_name is defined
            if (_isAnalyticsDebugEnabled) {
                console.log('sending uTag.view()');
            }
            uTag.view(data);
        }
        else {
            if (_isAnalyticsDebugEnabled) {
                console.log('sending uTag.link()');
            }
            uTag.link(data); // all other events
        }
    }
}

/**
 * ===================================================================================
 *  Public API with all methods for backward compatibility
 * ===================================================================================
 */

export interface IAnalyticsEvent {
    event: string; // needs to be defined for GTM
    on_page_event?: string;
    [key: string]: string | number | null | undefined;
}

export interface IAnalyticsView extends IAnalyticsEvent {
    page_name: string;
}

// main analytics tracking method for events
export function trackAnalyticsEvent(eventData: IAnalyticsEvent): void {
    trackAnalyticsAction(eventData);
}

// main analytics tracking method for page views
export function trackAnalyticsView(eventData: IAnalyticsView): void {
    if (!eventData.page_name || eventData.page_name.trim() === '') {
        console.error('trackAnalyticsView: invalid page_name attribute.', { eventData });
        return;
    }
    trackAnalyticsAction(eventData);
}

// --- Aliases for backward compatibility ---

export function FireTealiumEvent(event: IAnalyticsEvent): void {
    trackAnalyticsEvent(event);
}

export function FireAnalyticsTagEventOnSearch(searchEvent: string, searchKeyword: string): void {
    let eventData: IAnalyticsEvent = { event: searchEvent, on_page_event: searchEvent, search_kw: searchKeyword };
    if (searchEvent === 'search_refine') {
        const arr = searchKeyword.split('|');
        eventData = { event: searchEvent, on_page_event: 'search_refine', refine_content: arr[2], refine_type: arr[1], search_kw: arr[0] };
    }
    else if (searchEvent === 'search_suggested') {
        eventData = { event: searchEvent, on_page_event: 'search', search_kw: searchKeyword, suggested_search: 'true' };
    }
    trackAnalyticsEvent(eventData);
}

export function FireAnalyticsTagEventOnSearchResultItemClick(articleTitle: string, rank: string): void {
    trackAnalyticsEvent({ event: 'search_click',  on_page_event: 'search_click', search_item: articleTitle, search_result_rank: rank });
}

export function FireAnalyticsTagEventOnDownload(
    link: any, mediaType: string, contentId: string, mediaName: string, mediaLength: string, byline: string, pubYear: string, pubMonth: string, pubDay: string
): void {
    const fileUrl = link.href;
    trackAnalyticsEvent({
        byline,
        content_id: contentId,
        download_location: window.location.href,
        filename: fileUrl,
        media_filetype: fileUrl.substring(fileUrl.lastIndexOf('.') + 1).replace(/\?.*$/, ''),
        media_length: mediaLength,
        media_name: mediaName,
        media_type: mediaType,
        event: 'download_start',
        on_page_event: 'download_start',
        pub_day: pubDay,
        pub_month: pubMonth,
        pub_year: pubYear
    });
}

export function FireTealiumEventOnDownload(
    link: any, mediaType: string, contentId: string, mediaName: string, mediaLength: string, byline: string, pubYear: string, pubMonth: string, pubDay: string
): void {
    FireAnalyticsTagEventOnDownload(link, mediaType, contentId, mediaName, mediaLength, byline, pubYear, pubMonth, pubDay);
}

export function FireAnalyticsTagEventQuiz(surveyEvent: string, quizTitle: string): void {
    trackAnalyticsEvent({ event: surveyEvent, on_page_event: surveyEvent, surveyName: quizTitle });
}

// --- Exports for usage in other modules ---
export default {
    getAnalyticsPageData,
    trackAnalyticsEvent,
    trackAnalyticsView,
    FireAnalyticsTagEventOnDownload,
    FireAnalyticsTagEventOnSearch,
    FireAnalyticsTagEventOnSearchResultItemClick,
    FireAnalyticsTagEventQuiz,
    FireTealiumEvent,
    FireTealiumEventOnDownload
};



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./source/js/helpers/analytics.ts